// [PURPOSE]: Single source of truth for "is this a development build?"
// [WHY]: Phase B / #068 (closes COMING_SOON_ROADMAP §6.1). Pre-#068 the
//   raw `process.env.NODE_ENV === 'development'` check was duplicated
//   across 4 sites (SettingsPanel debug section, SettingsContext
//   `debugMode` default, SettingsContext legacy `isPremium` mock,
//   usePremiumAccess dev override). All four worked — CRA's
//   DefinePlugin replaces the literal at build time and the minifier
//   strips the dead production branches — but having one grep target
//   for "what's dev-only?" beats hunting through 4 places, and a
//   centralized constant is impossible to typo by accident.
//
// [INTEGRATION]: Imported wherever a dev-only branch needs gating:
//
//   ```jsx
//   import { IS_DEV_BUILD } from '../utils/devMode';
//   {IS_DEV_BUILD && <DebugPanel />}
//   ```
//
//   The expression `process.env.NODE_ENV === 'development'` constant-
//   folds to `false` in production, so the dead branch is dropped by
//   the minifier just as before. Build-time strip property is
//   preserved.
//
// [WHY NOT for JSX call sites]: Empirical testing during #068 found
//   that CRA's Webpack/Terser config does NOT reliably perform
//   cross-module constant folding for JSX expressions. A line like
//
//     {IS_DEV_BUILD && <DevPanel />}
//
//   leaves the dev component's strings (e.g. "Enable Debug Mode",
//   "Premium Testing") in the production bundle because the bundler
//   doesn't substitute the imported constant before tree-shaking.
//   The inline pattern
//
//     {process.env.NODE_ENV === 'development' && <DevPanel />}
//
//   does strip cleanly — DefinePlugin replaces the literal at build
//   time directly into the JSX, terser sees `"production" === "development"`,
//   constant-folds to `false`, and drops the createElement call.
//
//   Convention: use `IS_DEV_BUILD` for non-JSX runtime checks
//   (context defaults, hook logic, conditional fields) where the
//   single-source-of-truth + grep-target benefit applies. Keep the
//   inline `process.env.NODE_ENV === 'development'` literal at JSX
//   gating sites where bundle stripping matters. The mild duplication
//   trades against a real ~few-KB bundle saving + dev-string privacy.
//
// [WHY NOT a wrapper component]: An obvious extension is a
//   `<DevOnly>{children}</DevOnly>` wrapper. Same problem as the
//   constant-import pattern, plus JSX evaluates the children
//   expression at the call site (`React.createElement(...)` runs
//   regardless of whether DevOnly returns null at runtime). Skipped.

export const IS_DEV_BUILD = process.env.NODE_ENV === 'development';
